import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useServiceOfferingsService } from '../../service-offerings/services/serviceOfferingsService';
import { useSnackbar, type SnackbarState } from '../../../shared/hooks/useSnackbar';
import { extractErrorWithDetail } from '../../../shared/utils/errorUtils';
import type { ServiceConfiguration, ServiceOffering } from '../../../types/backend';

interface UseSubscribedOfferingsOptions {
  customerId: string | undefined;
  isActive: boolean;
}

export interface SubscribeArgs {
  offeringId: string;
  pinnedOfferingVersion: string;
  enabledCapabilities: string[];
}

export interface UseSubscribedOfferingsResult {
  subscriptions: ServiceConfiguration[];
  offerings: ServiceOffering[];
  offeringById: Map<string, ServiceOffering>;
  subscribableOfferings: ServiceOffering[];
  loading: boolean;
  error: string | null;
  busy: boolean;
  reload: () => Promise<void>;
  subscribe: (args: SubscribeArgs) => Promise<boolean>;
  revoke: (offeringId: string) => Promise<boolean>;
  toggleCapability: (offeringId: string, capabilityId: string, enabled: boolean) => Promise<void>;
  saveOperatorInputs: (offeringId: string, componentName: string, inputs: Record<string, string>) => Promise<boolean>;
  snackbar: SnackbarState;
  closeSnackbar: () => void;
}

export const subscribedOfferingKeys = {
  subscriptions: (customerId: string | undefined) =>
    ['service-administration', 'subscribed-offerings', customerId] as const,
  offerings: () => ['service-administration', 'offerings'] as const,
};

export function useSubscribedOfferings({
  customerId,
  isActive,
}: UseSubscribedOfferingsOptions): UseSubscribedOfferingsResult {
  const service = useServiceOfferingsService();
  const queryClient = useQueryClient();
  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();

  const subscriptionsQuery = useQuery({
    queryKey: subscribedOfferingKeys.subscriptions(customerId),
    queryFn: async () => (await service.listSubscribedOfferings(customerId as string)).service_configurations,
    enabled: isActive && !!customerId,
  });

  const offeringsQuery = useQuery({
    queryKey: subscribedOfferingKeys.offerings(),
    queryFn: async () => (await service.listOfferings()).service_offerings,
    enabled: isActive && !!customerId,
  });

  const subscriptions = useMemo(() => subscriptionsQuery.data ?? [], [subscriptionsQuery.data]);
  const offerings = useMemo(() => offeringsQuery.data ?? [], [offeringsQuery.data]);

  const loadError = subscriptionsQuery.error ?? offeringsQuery.error;
  const error = loadError ? extractErrorWithDetail(loadError, 'Failed to load subscribed offerings').message : null;

  const { refetch: refetchSubs } = subscriptionsQuery;
  const { refetch: refetchOfferings } = offeringsQuery;
  const reload = useCallback(async () => {
    await Promise.all([refetchSubs(), refetchOfferings()]);
  }, [refetchSubs, refetchOfferings]);

  const offeringById = useMemo(() => new Map(offerings.map((o) => [o.id, o])), [offerings]);

  const subscribableOfferings = useMemo(() => {
    const subscribedIds = new Set(subscriptions.map((s) => s.offering_id));
    return offerings.filter((o) => o.status === 'active' && !subscribedIds.has(o.id));
  }, [offerings, subscriptions]);

  const subscribeMutation = useMutation({
    mutationFn: ({ offeringId, pinnedOfferingVersion, enabledCapabilities }: SubscribeArgs) =>
      service.createSubscription({
        customer_id: customerId as string,
        offering_id: offeringId,
        pinned_offering_version: pinnedOfferingVersion,
        enabled_capabilities: enabledCapabilities,
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: subscribedOfferingKeys.subscriptions(customerId) }),
  });
  const { mutateAsync: subscribeAsync } = subscribeMutation;
  const subscribe = useCallback(
    async (args: SubscribeArgs) => {
      if (!customerId) return false;
      try {
        await subscribeAsync(args);
        showSnackbar('Subscribed — provisioning will begin shortly');
        return true;
      } catch (err) {
        showSnackbar(extractErrorWithDetail(err, 'Failed to subscribe').message, 'error');
        return false;
      }
    },
    [customerId, subscribeAsync, showSnackbar],
  );

  const revokeMutation = useMutation({
    mutationFn: (offeringId: string) => service.revokeSubscription(customerId as string, offeringId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: subscribedOfferingKeys.subscriptions(customerId) }),
  });
  const { mutateAsync: revokeAsync } = revokeMutation;
  const revoke = useCallback(
    async (offeringId: string) => {
      if (!customerId) return false;
      try {
        await revokeAsync(offeringId);
        showSnackbar('Subscription revoked');
        return true;
      } catch (err) {
        showSnackbar(extractErrorWithDetail(err, 'Failed to revoke subscription').message, 'error');
        return false;
      }
    },
    [customerId, revokeAsync, showSnackbar],
  );

  const toggleMutation = useMutation({
    mutationFn: ({ offeringId, capabilityId, enabled }: { offeringId: string; capabilityId: string; enabled: boolean }) =>
      service.toggleCapability({
        customer_id: customerId as string,
        offering_id: offeringId,
        capability_id: capabilityId,
        enabled,
      }),
    onSuccess: (updated, { offeringId }) => {
      queryClient.setQueryData<ServiceConfiguration[]>(
        subscribedOfferingKeys.subscriptions(customerId),
        (prev) => (prev ?? []).map((s) => (s.offering_id === offeringId ? updated : s)),
      );
    },
  });
  const { mutateAsync: toggleAsync } = toggleMutation;
  const toggleCapability = useCallback(
    async (offeringId: string, capabilityId: string, enabled: boolean) => {
      if (!customerId) return;
      try {
        await toggleAsync({ offeringId, capabilityId, enabled });
        showSnackbar(enabled ? 'Capability enabled' : 'Capability disabled');
      } catch (err) {
        showSnackbar(extractErrorWithDetail(err, 'Failed to update capability').message, 'error');
      }
    },
    [customerId, toggleAsync, showSnackbar],
  );

  const saveMutation = useMutation({
    mutationFn: ({ offeringId, componentName, inputs }: { offeringId: string; componentName: string; inputs: Record<string, string> }) =>
      service.setOperatorInputs({
        customer_id: customerId as string,
        offering_id: offeringId,
        component_name: componentName,
        inputs,
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: subscribedOfferingKeys.subscriptions(customerId) }),
  });
  const { mutateAsync: saveAsync } = saveMutation;
  const saveOperatorInputs = useCallback(
    async (offeringId: string, componentName: string, inputs: Record<string, string>) => {
      if (!customerId) return false;
      try {
        await saveAsync({ offeringId, componentName, inputs });
        showSnackbar('Operator inputs saved');
        return true;
      } catch (err) {
        showSnackbar(extractErrorWithDetail(err, 'Failed to save operator inputs').message, 'error');
        return false;
      }
    },
    [customerId, saveAsync, showSnackbar],
  );

  const loading = subscriptionsQuery.isLoading || offeringsQuery.isLoading;
  const busy =
    subscribeMutation.isPending ||
    revokeMutation.isPending ||
    toggleMutation.isPending ||
    saveMutation.isPending;

  return {
    subscriptions,
    offerings,
    offeringById,
    subscribableOfferings,
    loading,
    error,
    busy,
    reload,
    subscribe,
    revoke,
    toggleCapability,
    saveOperatorInputs,
    snackbar,
    closeSnackbar,
  };
}
